import { FileEdit, Sparkles, CheckSquare, Users, Send, Inbox, FileCheck2, GitCompareArrows, UserCheck, ShieldCheck } from "lucide-react";

const STEPS = [
  { Icon: FileEdit,         title: "Buyer creates a sourcing request",      body: "Describe the need in plain language or by voice." },
  { Icon: Sparkles,         title: "AI structures the requirements",         body: "Specifications, quantity, destination, deadline, budget and currency are extracted and shown back for review." },
  { Icon: CheckSquare,      title: "Buyer reviews and approves the RFQ",     body: "You edit any field. Nothing moves forward until you approve." },
  { Icon: Users,            title: "Buyer selects suppliers",                body: "Matched suppliers are shown with transparent trust levels. You choose who to invite." },
  { Icon: Send,             title: "RFQ is sent via the available channel",  body: "Today the RFQ is routed in-platform; email dispatch is on the roadmap." },
  { Icon: Inbox,            title: "Supplier receives and reviews the RFQ",  body: "The supplier opens the RFQ inside GlobalBuy AI. Opens are recorded only when verifiable." },
  { Icon: FileCheck2,       title: "Supplier responds with a quotation",     body: "Price, MOQ, lead time, Incoterms, payment terms, warranty and notes." },
  { Icon: GitCompareArrows, title: "Quotations appear side-by-side",         body: "Compare suppliers transparently with AI analysis — strengths, weaknesses, risk." },
  { Icon: UserCheck,        title: "A human makes the final decision",       body: "GlobalBuy AI never purchases or commits payment on your behalf." },
];

export function WorkflowSteps() {
  return (
    <section id="workflow" className="border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4 md:sticky md:top-24">
            <div className="eyebrow mb-3">How your RFQ reaches a supplier</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-white">
              A clear path from need<br/><span className="text-[hsl(var(--gold))]">to quotation.</span>
            </h2>
            <p className="text-sm text-slate-400 mt-5 leading-relaxed">
              Every stage is visible to you. No hidden autonomous steps, no fabricated statuses.
            </p>
            <div className="mt-6 rounded-md border border-[hsl(var(--gold))]/30 bg-[hsl(var(--gold))]/5 p-4">
              <div className="flex items-start gap-2.5">
                <ShieldCheck size={16} className="text-[hsl(var(--gold))] mt-0.5 shrink-0"/>
                <p className="text-[13px] text-slate-200 leading-relaxed">
                  GlobalBuy AI only shows communication states that the system can actually verify. It never fabricates supplier delivery, views, responses, verification, or quotations.
                </p>
              </div>
            </div>
          </div>

          <div className="md:col-span-8">
            <ol className="relative border-l border-white/10 ml-3 space-y-5">
              {STEPS.map(({ Icon, title, body }, i) => (
                <li key={title} className="pl-8" data-testid={`workflow-step-${i+1}`}>
                  <span className="absolute -left-[13px] mt-1 h-6 w-6 rounded-full bg-[hsl(var(--navy))] border border-[hsl(var(--gold))]/40 text-[hsl(var(--gold))] flex items-center justify-center">
                    <Icon size={12}/>
                  </span>
                  <div className="rounded-md border border-white/10 bg-[hsl(var(--navy-muted))]/70 backdrop-blur-sm p-4 hover:border-white/20 transition-colors">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[11px] font-mono text-[hsl(var(--gold))]/80">{String(i+1).padStart(2,"0")}</span>
                      <div className="font-semibold tracking-tight text-slate-100">{title}</div>
                    </div>
                    <p className="text-[13px] text-slate-400 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
